import {
  gpcApi,
  pricingBasePath,
  propertyBasePath,
  propertyConfigPath,
  gprtPricingRequestPath,
  sarahRfpDecisionPath,
  sarahRfpReviewStatusPath,
  sarahRfpReviewStatusBatchPath,
  platformsPath,
  autoApprovalConfigsPath,
  autoApprovalConfigPath,
} from "./ApiClient";
import type {
  GpcPropertyConfig,
  GpcRoomType,
  GpcRoomAvailability,
  GpcPropertyAvailability,
  UpdatePropertyConfigPayload,
  PlatformDto,
  AutoApprovalConfigDto,
  CreatePricingRequestPayload,
  CreatePricingRequestResponse,
  PricingRequestListResponse,
  PricingRequestDetail,
  QuotedItinerariesResponse,
  RequestSummaryResponse,
  AuditsResponse,
  ListRequestsParams,
  AuditQueryParams,
  UpdateDecisionPayload,
  UpdateDecisionResponse,
  UpdateSarahDecisionPayload,
  RecalculatePricingRequestPayload,
  PricingRequestDetailWithGprtContext,
  SubmitManualRatePayload,
  SarahReviewStatus,
  PricingRequestListItem,
} from "./types";
import { SARAH_SOURCE } from "./types";
import type {
  BackendPageResponse,
  BackendPricingRequestItem,
  BackendItineraryListResponse,
  BackendSummaryResponse,
  BackendAuditListResponse,
  BackendPropertyConfig,
  BackendPricingRequestDetailWithContext,
  BackendManualRateItineraryLine,
  BackendSarahReviewStatus,
  BackendGpcReviewStatusItem,
} from "./backendTypes";
import {
  adaptListResponse,
  adaptDetailResponse,
  adaptItinerariesResponse,
  adaptSummaryResponse,
  adaptAuditsResponse,
  adaptPropertyConfig,
  adaptCreatePayload,
  adaptListParams,
  adaptPricingRequestDetailWithContext,
  adaptSarahReviewStatus,
} from "./adapters";
import { logError } from "@/utils/errorHandler";

// ──────────────────────────────────────────────
// Platforms
// ──────────────────────────────────────────────

export async function fetchPlatforms(
  propertyId: string
): Promise<PlatformDto[]> {
  const response = await gpcApi.get<PlatformDto[]>(platformsPath(propertyId));
  return response.data ?? [];
}

// ──────────────────────────────────────────────
// Property Config
// ──────────────────────────────────────────────

export async function getPropertyConfig(
  propertyId: string
): Promise<GpcPropertyConfig> {
  const response = await gpcApi.get<BackendPropertyConfig>(
    propertyConfigPath(propertyId)
  );
  if (!response.data)
    throw new Error("Empty response from server for property config");
  return adaptPropertyConfig(response.data);
}

export async function updatePropertyConfig(
  propertyId: string,
  payload: UpdatePropertyConfigPayload
): Promise<GpcPropertyConfig> {
  const response = await gpcApi.patch<BackendPropertyConfig>(
    propertyConfigPath(propertyId),
    payload
  );
  if (!response.data)
    throw new Error("Empty response from server for property config update");
  return adaptPropertyConfig(response.data);
}

// ──────────────────────────────────────────────
// Per-Property/Platform Auto-Approval
// ──────────────────────────────────────────────

export async function listAutoApprovalConfigs(
  propertyId: string
): Promise<AutoApprovalConfigDto[]> {
  const response = await gpcApi.get<AutoApprovalConfigDto[]>(
    autoApprovalConfigsPath(propertyId)
  );
  return response.data ?? [];
}

/** 404s if no row exists for this (property, platform) pair — there is no create path. */
export async function updateAutoApprovalConfig(
  propertyId: string,
  platformId: number,
  autoApprovalEnabled: boolean
): Promise<AutoApprovalConfigDto> {
  const response = await gpcApi.patch<AutoApprovalConfigDto>(
    autoApprovalConfigPath(propertyId, platformId),
    { autoApprovalEnabled }
  );
  if (!response.data)
    throw new Error("Empty response from server for auto-approval update");
  return response.data;
}

// ──────────────────────────────────────────────
// Room Types + Availability
// ──────────────────────────────────────────────

export async function fetchRoomTypes(
  propertyId: string
): Promise<GpcRoomType[]> {
  const response = await gpcApi.get<GpcRoomType[]>(
    `${propertyBasePath(propertyId)}/room-types`
  );
  return response.data ?? [];
}

export async function fetchRoomAvailability(
  propertyId: string,
  startDate: string,
  endDate: string
): Promise<GpcRoomAvailability[]> {
  const response = await gpcApi.get<GpcRoomAvailability[]>(
    `${propertyBasePath(propertyId)}/room-type-availability`,
    { params: { startDate, endDate } }
  );
  return response.data ?? [];
}

/** Kept apart from the room-type read so one outage can't take both levels down. */
export async function fetchPropertyAvailability(
  propertyId: string,
  startDate: string,
  endDate: string
): Promise<GpcPropertyAvailability[]> {
  const response = await gpcApi.get<GpcPropertyAvailability[]>(
    `${propertyBasePath(propertyId)}/property-availability`,
    { params: { startDate, endDate } }
  );
  return response.data ?? [];
}

// ──────────────────────────────────────────────
// Pricing Requests
// ──────────────────────────────────────────────

export async function createPricingRequest(
  propertyId: string,
  payload: CreatePricingRequestPayload
): Promise<CreatePricingRequestResponse> {
  const body = adaptCreatePayload(payload, propertyId);
  const response = await gpcApi.post<CreatePricingRequestResponse>(
    `${pricingBasePath(propertyId)}/requests`,
    body
  );
  if (!response.data)
    throw new Error("Empty response from server for pricing request creation");
  return response.data;
}

export async function listPricingRequests(
  propertyId: string,
  params?: ListRequestsParams
): Promise<PricingRequestListResponse> {
  const response = await gpcApi.get<
    BackendPageResponse<BackendPricingRequestItem>
  >(`${pricingBasePath(propertyId)}/requests`, {
    params: adaptListParams(params),
  });
  if (!response.data)
    throw new Error("Empty response from server for pricing requests list");
  return adaptListResponse(response.data);
}

export async function listPricingRequestsAcrossProperties(
  propertyIds: string[],
  params?: ListRequestsParams
): Promise<PricingRequestListResponse> {
  if (propertyIds.length === 0) {
    throw new Error(
      "listPricingRequestsAcrossProperties requires at least one propertyId"
    );
  }
  const response = await gpcApi.get<
    BackendPageResponse<BackendPricingRequestItem>
  >("/pricing/requests", {
    params: { ...adaptListParams(params), propertyIds: propertyIds.join(",") },
  });
  if (!response.data)
    throw new Error("Empty response from server for pricing requests list");
  return adaptListResponse(response.data);
}

export async function getPricingRequestDetail(
  propertyId: string,
  requestId: string
): Promise<PricingRequestDetail> {
  const response = await gpcApi.get<BackendPricingRequestItem>(
    `${pricingBasePath(propertyId)}/requests/${requestId}`
  );
  if (!response.data)
    throw new Error("Empty response from server for pricing request detail");
  return adaptDetailResponse(response.data);
}

export async function recalculatePricingRequest(
  propertyId: string,
  requestId: string,
  payload: RecalculatePricingRequestPayload
): Promise<PricingRequestDetail> {
  const response = await gpcApi.post<BackendPricingRequestItem>(
    `${pricingBasePath(propertyId)}/requests/${requestId}/calculate`,
    payload
  );
  if (!response.data)
    throw new Error("Empty response from server for recalculation");
  return adaptDetailResponse(response.data);
}

export async function updateDecision(
  propertyId: string,
  requestId: string,
  payload: UpdateDecisionPayload
): Promise<UpdateDecisionResponse> {
  const response = await gpcApi.patch<UpdateDecisionResponse>(
    `${pricingBasePath(propertyId)}/requests/${requestId}/decision`,
    payload
  );
  if (!response.data)
    throw new Error("Empty response from server for decision update");
  return response.data;
}

/**
 * SARAH-sourced requests only — unlike updateDecision, approving/declining here
 * fires the RFP state machine's APPROVE_HITL/DECLINE_HITL event and, on approve,
 * resumes the SARAH pipeline to submit the quote.
 *
 * 204 No Content always — no body to parse; caller must refetch to see the outcome.
 */
export async function updateSarahDecision(
  pricingRequestId: string,
  payload: UpdateSarahDecisionPayload
): Promise<void> {
  await gpcApi.patch(sarahRfpDecisionPath(pricingRequestId), payload);
}

/**
 * SARAH-sourced requests only — the one place pendingGpcReviewType/
 * sarahResumeStatus/sarahResumeError actually live (kept off
 * PricingRequestDetailDto for module-boundary reasons). 200 always, all-null
 * for a non-SARAH row or one with no RFP linked yet — safe to call unconditionally.
 */
export async function getSarahReviewStatus(
  pricingRequestId: string
): Promise<SarahReviewStatus> {
  const response = await gpcApi.get<BackendSarahReviewStatus>(
    sarahRfpReviewStatusPath(pricingRequestId)
  );
  return adaptSarahReviewStatus(response.data ?? {});
}

/**
 * Batch sibling for the list table/sidebar badge — one call per page of up
 * to 500 ids instead of one call per row. Response is 1:1 with the input
 * ids (never omits one), so the returned map always has an entry for every
 * id passed in. Caller must guard the zero-ids case — an empty id list 400s
 * server-side same as exceeding the 500 cap.
 */
export async function getSarahReviewStatusBatch(
  pricingRequestIds: string[]
): Promise<Map<string, SarahReviewStatus>> {
  if (pricingRequestIds.length === 0) return new Map();
  const response = await gpcApi.get<BackendGpcReviewStatusItem[]>(
    sarahRfpReviewStatusBatchPath(),
    { params: { pricingRequestIds: pricingRequestIds.join(",") } }
  );
  return new Map(
    (response.data ?? [])
      .filter((item): item is BackendGpcReviewStatusItem => !!item)
      .map((item) => [
        item.pricingRequestId,
        adaptSarahReviewStatus(item.status ?? {}),
      ])
  );
}

// Backend's MAX_PRICING_REQUEST_IDS cap (RfpPricingDecisionController) is 500,
// but ids are sent as a comma-joined query param, not a POST body — 500 UUIDs
// is ~18KB, past the default header/URL limits of common gateways (nginx,
// ALB). Chunk well under that cap; ~100 ids keeps the query string under 4KB.
// PricingRequestsTable's "Load more" can grow fetchSize well past this, which
// is why chunking into parallel, independently-failing batch calls exists.
const SARAH_REVIEW_STATUS_BATCH_SIZE = 100;

/**
 * Shared by PricingRequestsTable and useGpcPendingRequestsCount — both need
 * the same "fetch, chunk at the cap, merge pendingGpcReviewType back onto
 * rows" shape, just over a different id list. Never throws: a failed chunk
 * is logged and its rows are left as the server sent them, so callers don't
 * need their own try/catch around this.
 */
export async function enrichRowsWithSarahReviewType(
  rows: PricingRequestListItem[]
): Promise<PricingRequestListItem[]> {
  if (rows.length === 0) return rows;
  const sarahRows = rows.filter((row) => row.requestSource === SARAH_SOURCE);
  if (sarahRows.length === 0) return rows;
  const ids = sarahRows.map((row) => row.id);
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += SARAH_REVIEW_STATUS_BATCH_SIZE) {
    chunks.push(ids.slice(i, i + SARAH_REVIEW_STATUS_BATCH_SIZE));
  }
  const results = await Promise.allSettled(
    chunks.map((chunk) => getSarahReviewStatusBatch(chunk))
  );
  const statusMap = new Map<string, SarahReviewStatus>();
  results.forEach((result, idx) => {
    if (result.status === "fulfilled") {
      result.value.forEach((value, key) => statusMap.set(key, value));
    } else {
      logError(
        result.reason,
        `GPC: Failed to load SARAH review status batch (chunk ${idx + 1}/${
          chunks.length
        })`
      );
    }
  });
  return rows.map((row) => {
    const status = statusMap.get(row.id);
    return status
      ? { ...row, pendingGpcReviewType: status.pendingGpcReviewType }
      : row;
  });
}

// ──────────────────────────────────────────────
// GPRT-adjacent pricing endpoints (contract endpoints 6-8) — authorized via
// GPC roles, operate on the pricing request id, but live under /gprt/*.
// ──────────────────────────────────────────────

export async function getGprtPricingRequestDetail(
  propertyId: string,
  requestId: string
): Promise<PricingRequestDetailWithGprtContext> {
  const response = await gpcApi.get<BackendPricingRequestDetailWithContext>(
    gprtPricingRequestPath(propertyId, requestId)
  );
  if (!response.data)
    throw new Error(
      "Empty response from server for GPRT pricing request detail"
    );
  return adaptPricingRequestDetailWithContext(response.data);
}

export async function submitManualRate(
  propertyId: string,
  requestId: string,
  payload: SubmitManualRatePayload
): Promise<PricingRequestDetail> {
  const itineraries: BackendManualRateItineraryLine[] = payload.itineraries.map(
    (it) => ({
      requestItineraryId: it.requestItineraryId ?? undefined,
      checkinDate: it.checkinDate,
      checkoutDate: it.checkoutDate,
      roomCategory: it.roomCategory,
      // manualRateLineError already gates submission on every line having a
      // non-null price — this fallback only protects the wire type.
      price: it.price ?? 0,
      roomCount: it.roomCount,
    })
  );
  const response = await gpcApi.post<BackendPricingRequestItem>(
    `${gprtPricingRequestPath(propertyId, requestId)}/manualRate`,
    {
      itineraries,
      decision: "MANUAL_RATE",
      decisionComments: payload.decisionComments || undefined,
    }
  );
  if (!response.data)
    throw new Error("Empty response from server for manual rate submission");
  return adaptDetailResponse(response.data);
}

/** 200 OK with an empty body (contract) — caller must re-fetch to see the
 * persisted decision/comment/rateAcknowledgedBy. */
export async function updateGprtDecision(
  propertyId: string,
  requestId: string,
  payload: UpdateDecisionPayload
): Promise<void> {
  await gpcApi.patch(
    `${gprtPricingRequestPath(propertyId, requestId)}/decision`,
    payload
  );
}

// ──────────────────────────────────────────────
// Itineraries + Market Snapshot
// ──────────────────────────────────────────────

export async function getItineraries(
  propertyId: string,
  requestId: string,
  params?: { stayDate?: string }
): Promise<QuotedItinerariesResponse> {
  const response = await gpcApi.get<BackendItineraryListResponse>(
    `${pricingBasePath(propertyId)}/requests/${requestId}/itineraries`,
    { params }
  );
  return adaptItinerariesResponse(response.data, requestId);
}

// ──────────────────────────────────────────────
// Summary
// ──────────────────────────────────────────────

export async function getRequestSummary(
  propertyId: string,
  requestId: string
): Promise<RequestSummaryResponse> {
  const response = await gpcApi.get<BackendSummaryResponse>(
    `${pricingBasePath(propertyId)}/requests/${requestId}/summary`
  );
  if (!response.data)
    throw new Error("Empty response from server for request summary");
  return adaptSummaryResponse(response.data, requestId);
}

// ──────────────────────────────────────────────
// Audits
// ──────────────────────────────────────────────

export async function getAudits(
  propertyId: string,
  requestId: string,
  params?: AuditQueryParams
): Promise<AuditsResponse> {
  const response = await gpcApi.get<BackendAuditListResponse>(
    `${pricingBasePath(propertyId)}/requests/${requestId}/audits`,
    { params }
  );
  return adaptAuditsResponse(response.data, requestId);
}
